import DOMPurify from "dompurify";

export const REVIEW_TEMPLATE_TOKENS = [
  "review_date",
  "change_direction",
  "residual_score",
  "band_label",
  "risk_title",
  "risk_id",
  "residual_score_from",
  "residual_score_to",
  "band_label_from",
  "band_label_to",
  "residual_likelihood_from",
  "residual_likelihood_to",
  "residual_impact_from",
  "residual_impact_to",
  "impact_theme",
  "review_changes_summary",
];

export const CONTROL_REVIEW_TEMPLATE_TOKENS = [
  "review_date",
  "control_title",
  "control_id",
  "reviewer",
  "review_outcome_from",
  "review_outcome_to",
  "effectiveness_from",
  "effectiveness_to",
  "review_changes_summary",
];

const RISK_REVIEW_TEMPLATE_TOKEN_LABELS = {
  review_date: "Review Date",
  change_direction: "Change Direction",
  residual_score: "Residual Score",
  band_label: "Band Label",
  risk_title: "Risk Title",
  risk_id: "Risk ID",
  residual_score_from: "Residual Score From",
  residual_score_to: "Residual Score To",
  band_label_from: "Band Label From",
  band_label_to: "Band Label To",
  residual_likelihood_from: "Residual Likelihood From",
  residual_likelihood_to: "Residual Likelihood To",
  residual_impact_from: "Residual Impact From",
  residual_impact_to: "Residual Impact To",
  impact_theme: "Impact Theme",
  review_changes_summary: "Review Changes Summary",
};

const CONTROL_REVIEW_TEMPLATE_TOKEN_LABELS = {
  review_date: "Review Date",
  control_title: "Control Title",
  control_id: "Control ID",
  reviewer: "Reviewer",
  review_outcome_from: "Review Outcome From",
  review_outcome_to: "Review Outcome To",
  effectiveness_from: "Effectiveness From",
  effectiveness_to: "Effectiveness To",
  review_changes_summary: "Review Changes Summary",
};

export const REVIEW_TEMPLATE_TOKEN_LABELS = {
  ...RISK_REVIEW_TEMPLATE_TOKEN_LABELS,
  ...CONTROL_REVIEW_TEMPLATE_TOKEN_LABELS,
};

export const REVIEW_TEMPLATE_PLACEHOLDERS = REVIEW_TEMPLATE_TOKENS.map((name) => ({
  name,
  label: REVIEW_TEMPLATE_TOKEN_LABELS[name] || name,
}));

export const CONTROL_REVIEW_TEMPLATE_PLACEHOLDERS = CONTROL_REVIEW_TEMPLATE_TOKENS.map((name) => ({
  name,
  label: REVIEW_TEMPLATE_TOKEN_LABELS[name] || name,
}));

const ALLOWED_TAGS = ["p", "strong", "em", "u", "ul", "li", "span", "br"];
const ALLOWED_ATTR = ["data-name", "class"];
const ALL_REVIEW_TEMPLATE_TOKENS = Array.from(new Set([...REVIEW_TEMPLATE_TOKENS, ...CONTROL_REVIEW_TEMPLATE_TOKENS]));
const TOKEN_NAME_PATTERN = ALL_REVIEW_TEMPLATE_TOKENS.join("|");
const LEGACY_TOKEN_REGEX = new RegExp(`{{\\s*(${TOKEN_NAME_PATTERN})\\s*}}`, "gi");

export const normalizeTemplateTokenName = (tokenName) =>
  String(tokenName || "")
    .trim()
    .toLowerCase()
    .replace(/^\{\{\s*/, "")
    .replace(/\s*\}\}$/, "");

export const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export const sanitizeRichTextHtml = (value) =>
  DOMPurify.sanitize(String(value || ""), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });

export const createPlaceholderTokenHtml = (tokenName) => {
  const name = normalizeTemplateTokenName(tokenName);
  if (!name || !ALL_REVIEW_TEMPLATE_TOKENS.includes(name)) return "";
  const label = REVIEW_TEMPLATE_TOKEN_LABELS[name] || name;
  return `<span class="template-token" data-name="${escapeHtml(name)}">${escapeHtml(label)}</span>`;
};

const replaceLegacyTokenTextWithChipHtml = (value) =>
  String(value || "").replace(LEGACY_TOKEN_REGEX, (_match, tokenName) =>
    createPlaceholderTokenHtml(tokenName)
  );

export const richTextFromPlainText = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "<p></p>";
  const escaped = escapeHtml(text).replace(/\n/g, "<br>");
  return `<p>${escaped}</p>`;
};

export const ensureRichTextHtml = (value) => {
  const raw = String(value ?? "");
  const withTokenChips = replaceLegacyTokenTextWithChipHtml(raw);
  const html = /<\/?[a-z][\s\S]*>/i.test(withTokenChips) ? withTokenChips : richTextFromPlainText(withTokenChips);
  const sanitized = sanitizeRichTextHtml(html);
  return sanitized || "<p></p>";
};

export const isRichTextEmpty = (value) => {
  const sanitized = sanitizeRichTextHtml(value);
  const text = sanitized
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/?[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .trim();
  return !text;
};

export const richTextToPlainText = (value) => {
  const sanitized = sanitizeRichTextHtml(value);
  const withBreaks = sanitized
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<li>/gi, "• ");

  if (typeof DOMParser === "function") {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${withBreaks}</div>`, "text/html");
    const text = String(doc.body?.textContent || "");
    return text
      .replace(/<\/?[a-z][^>]*>/gi, " ")
      .replace(/\u00a0/g, " ")
      .replace(/\r\n/g, "\n")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n[ \t]+/g, "\n")
      .replace(/[ \t]{2,}/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }

  return withBreaks
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/<\/?[a-z][^>]*>/gi, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

export const replaceTemplatePlaceholders = (template, variables) => {
  let rendered = sanitizeRichTextHtml(template);
  let replacedSpanTokens = false;
  if (typeof DOMParser === "function") {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${rendered}</div>`, "text/html");
    const host = doc.body?.firstElementChild;
    if (host) {
      host.querySelectorAll("span[data-name]").forEach((node) => {
        const tokenName = normalizeTemplateTokenName(node.getAttribute("data-name"));
        const replacement = escapeHtml(variables?.[tokenName] ?? "");
        const textNode = doc.createTextNode(replacement);
        node.replaceWith(textNode);
        replacedSpanTokens = true;
      });
      rendered = host.innerHTML;
    }
  }
  if (!replacedSpanTokens) {
    rendered = rendered.replace(/<span[^>]*data-name="([^"]+)"[^>]*>[\s\S]*?<\/span>/gi, (_match, tokenName) =>
      escapeHtml(variables?.[normalizeTemplateTokenName(tokenName)] ?? "")
    );
  }
  rendered = rendered.replace(LEGACY_TOKEN_REGEX, (_match, tokenName) =>
    escapeHtml(variables?.[normalizeTemplateTokenName(tokenName)] ?? "")
  );
  return sanitizeRichTextHtml(rendered);
};

export const extractTemplateTokenNames = (template, allowedTokens = ALL_REVIEW_TEMPLATE_TOKENS) => {
  const tokenSet = new Set();
  const allowed = new Set((allowedTokens || []).map((token) => normalizeTemplateTokenName(token)).filter(Boolean));
  const sanitized = sanitizeRichTextHtml(template);

  sanitized.replace(/data-name="([^"]+)"/gi, (_match, tokenName) => {
    const normalized = normalizeTemplateTokenName(tokenName);
    if (normalized && allowed.has(normalized)) {
      tokenSet.add(normalized);
    }
    return _match;
  });

  sanitized.replace(LEGACY_TOKEN_REGEX, (_match, tokenName) => {
    const normalized = normalizeTemplateTokenName(tokenName);
    if (normalized && allowed.has(normalized)) {
      tokenSet.add(normalized);
    }
    return _match;
  });

  return tokenSet;
};
